/**
 * गोत्र — first 45.6 seconds.  "LIT ARCHIVE"
 * =========================================================================
 * Hand-authored 2026-08-15. ⛔ Nothing is reused from VerA/VerB/VerC/VerD or kit45 —
 * those were rejected outright. Reused DATA only: the Charon narration, the globally
 * force-aligned word timings, the 8 depth-sliced plates, the cutout library.
 *
 * WHY THIS ONE IS DIFFERENT FROM THE NINETEEN BEFORE IT
 * -----------------------------------------------------
 * 1. THE CAST STANDS INSIDE THE PHOTOGRAPH.  Every plate is 5 inpainted depth slices;
 *    the figures are injected between slice 2 and slice 3, so the near ground occludes
 *    them and they cast a real shadow onto what is behind. That is the answer to
 *    "empty canvas" — the canvas is a room, not a background, and it is full at every
 *    depth. It is also the answer to "3d effects" without a single AI video frame.
 *
 * 2. ONE OBJECT CARRIES THE WHOLE 45 SECONDS.  A single amber thread is the floor
 *    line, then the phone cord, then the corridor's perspective line, then the thing
 *    that dives into the banyan roots on the word जड़, then the spine between the two
 *    व्यवस्थाएँ, then the lineage sutra that beads out into पिता → उनके पिता. One
 *    instance, six meanings, never re-introduced. (LAW 3, applied at film scale.)
 *
 * 3. THE GROUND NEVER MOVES WHILE A BEAT IS RESIDENT.  Round-10 rule 6. It only
 *    travels across a cut — and when it does, it dollies THROUGH the near slices,
 *    which is where the depth is spent. 8 plates, 7 transitions, zero drift.
 *
 * 4. TYPE IS A PAIRING, NOT A SIZE RAMP.  Anek Devanagari 300–800 is the channel's
 *    own voice; Tiro Devanagari Hindi (calligraphic, one weight) is used ONLY for
 *    words that belong to the tradition — जड़, आर्ष, ऋषि. Two voices, so the frame
 *    can say who is speaking without a caption saying it.
 *
 * 5. TEXT BUDGET IS ENFORCED BY THE CUT.  Three of the eight frames carry no headline
 *    at all. The longest headline is four words. Everything else is spoken.
 *
 * BEATS — asserted non-overlapping at module load (v17/v18 rendered 7 seconds of two
 * beats at once because nobody checked the arithmetic).
 */
import React from "react";
import { AbsoluteFill, Audio, Img, interpolate, staticFile, useVideoConfig } from "remotion";
import {
  anek,
  assertNoOverlap,
  Atmosphere,
  Bead,
  beadAt,
  C,
  Cast,
  Curve,
  devaBlock,
  halo,
  Kicker,
  Line,
  Rail,
  RISE,
  Scene,
  STAMP,
  Tag,
  Thread,
  tiro,
  useCurve,
  useKeys,
  useKeysLinear,
  useSpringAt,
  useSwing,
  useT,
  W,
} from "./g45kit";
import { WORDS } from "./words45";

const w = (a: number, b: number): W[] => WORDS.slice(a, b + 1) as W[];

/* ═══════════════════════════════════════════════════════════════════════════
   THE FRAME TABLE
   One entry per PLATE, not per idea — frames 1 holds two ideas on one ground so
   that the courtyard never cuts while the pandit is still asking his question.
   ═══════════════════════════════════════════════════════════════════════════ */
const F = {
  courtyard: { id: "courtyard", a: 0.0, b: 12.1, scene: "A1_courtyard" },
  phone: { id: "phone", a: 12.1, b: 15.85, scene: "A3_phonecall" },
  corridor: { id: "corridor", a: 15.85, b: 19.0, scene: "A6_corridor" },
  pillars: { id: "pillars", a: 19.0, b: 22.6, scene: "A5_three_pillars" },
  register: { id: "register", a: 22.6, b: 29.7, scene: "A2_register" },
  roots: { id: "roots", a: 29.7, b: 33.05, scene: "A7_banyan_roots" },
  lamp: { id: "lamp", a: 33.05, b: 38.55, scene: "A4_lamp_void" },
  column: { id: "column", a: 38.55, b: 45.7, scene: "A8_ancestor_column" },
} as const;

assertNoOverlap(Object.values(F));

const LEAD_IN = 0.3;
const LEAD_OUT = 0.4;

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME — ground + cast + type, with the dolly-through transition.

   entry:  the plate arrives already pushed in and SETTLES over 0.34s
   body:   push is literally the same number for the whole beat → dead static
   exit:   the near slices fly past the camera; because they are separate depth
           slices this reads as travelling through the room, not as a dissolve
   ═══════════════════════════════════════════════════════════════════════════ */
const Frame: React.FC<{
  def: { id: string; a: number; b: number; scene: string };
  first?: boolean;
  insertAfter?: number;
  pan?: number;
  cast?: React.ReactNode;
  over?: React.ReactNode;
}> = ({ def, first, insertAfter = 2, pan = 0, cast, over }) => {
  const { a, b, scene } = def;
  const pushIn = useKeys([
    [a - LEAD_IN, first ? 0.34 : 0.62],
    [a + (first ? 0.9 : 0.34), 0],
  ]);
  const pushOut = useKeys([
    [b - 0.02, 0],
    [b + LEAD_OUT, 1.25],
  ]);
  const op = useKeysLinear([
    [a - LEAD_IN, first ? 1 : 0],
    [a - 0.02, 1],
    [b + 0.1, 1],
    [b + LEAD_OUT, 0],
  ]);
  const contentOp = useKeysLinear([
    [b - 0.05, 1],
    [b + 0.22, 0],
  ]);
  return (
    <AbsoluteFill style={{ opacity: op }}>
      <Scene
        scene={scene}
        push={pushIn + pushOut}
        pan={pan}
        insertAfter={insertAfter}
      >
        <AbsoluteFill style={{ opacity: contentOp }}>{cast}</AbsoluteFill>
      </Scene>
      <AbsoluteFill style={{ zIndex: 10, opacity: contentOp }}>{over}</AbsoluteFill>
    </AbsoluteFill>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 1 · THE COURTYARD  0.00 – 12.10
   Two ideas, one unbroken ground.

   idea A (0.0–7.0)   GRID → STRIP.  Yash's device. Six occasions arrive BIG in a
                      3-across grid that fills the canvas, each on the frame its own
                      word is spoken, then all six travel down into a bottom strip
                      together on "संस्कार". One instance each — never a big copy
                      swapped for a small one.
   idea B (7.0–12.1)  The pandit walks into the same courtyard, the six occasions
                      still docked beneath him, and asks the question. The docked
                      strip is WHY the question lands: he is asking it of all six.
   ═══════════════════════════════════════════════════════════════════════════ */

/** grid pose → strip pose, as one travelling object */
const Occasion: React.FC<{
  src: string;
  at: number;
  gx: number;
  gy: number;
  gh: number;
  sx: number;
  idx: number;
}> = ({ src, at, gx, gy, gh, sx, idx }) => {
  const DOCK = 6.28; // "संस्कार" — the whole row leaves together
  const DOCKED = 7.05;
  const sw = useSwing(at, undefined, { from: -15, drop: 66, idle: 0 });
  const x = useKeys([
    [at, gx],
    [DOCK, gx],
    [DOCKED, sx],
  ]);
  const y = useKeys([
    [at, gy],
    [DOCK, gy],
    [DOCKED, 684],
  ]);
  const h = useKeys([
    [at, gh],
    [DOCK, gh],
    [DOCKED, 78],
  ]);
  // once docked they are context, not subject — they step back in the light
  const dim = useKeys([
    [DOCK, 1],
    [DOCKED, 0.74],
    [10.6, 0.74],
    [11.0, 0.44],
  ]);
  return (
    <Cast
      src={src}
      x={x}
      y={y}
      h={h}
      rot={sw.rot * (1 - useKeys([[DOCK, 0], [DOCKED, 1]]))}
      opacity={sw.opacity * dim}
      audit={`occ${idx}`}
    />
  );
};

const OCCASIONS: { src: string; at: number; label: string; gx: number; gy: number; gh: number; sx: number }[] = [
  { src: "bi_cradle", at: 0.84, label: "जन्म", gx: 250, gy: 306, gh: 172, sx: 152 },
  { src: "rt_razor", at: 1.58, label: "मुंडन", gx: 640, gy: 306, gh: 156, sx: 332 },
  { src: "rt_mandap", at: 2.4, label: "शादी", gx: 1030, gy: 306, gh: 178, sx: 512 },
  { src: "rt_threshold", at: 3.28, label: "गृह प्रवेश", gx: 250, gy: 596, gh: 150, sx: 692 },
  { src: "rt_havan", at: 4.26, label: "हवन", gx: 640, gy: 596, gh: 176, sx: 872 },
  { src: "rt_pot", at: 5.94, label: "अंतिम संस्कार", gx: 1030, gy: 596, gh: 152, sx: 1052 },
];

const CourtyardCast: React.FC = () => {
  const pandit = useSwing(8.69, undefined, { from: -11, drop: 74, idle: 0.55 });
  return (
    <>
      {OCCASIONS.map((o, i) => (
        <Occasion key={o.src} {...o} idx={i} />
      ))}
      {/* the pandit — the one thing that moves boldly in the second half of this frame */}
      <Cast
        src="pp_pandit"
        x={985}
        y={628 + pandit.dy * 0.35}
        h={392}
        rot={pandit.rot}
        opacity={pandit.opacity}
        scale={pandit.scale}
        audit="pandit"
      />
    </>
  );
};

const CourtyardType: React.FC = () => {
  // labels live only while the objects are big; the strip is wordless on purpose
  const labelOut = 6.28;
  return (
    <>
      {OCCASIONS.map((o) => (
        <Tag
          key={o.label}
          at={o.at}
          out={labelOut}
          text={o.label}
          x={o.gx}
          y={o.gy + 16}
          size={o.label.length > 6 ? 27 : 31}
          weight={700}
          audit={`lbl:${o.label}`}
        />
      ))}
      <div style={{ position: "absolute", left: 84, top: 208, maxWidth: 640 }}>
        <Kicker at={7.99} out={10.35} text="हर बार · सबसे पहले" size={26} />
        <div style={{ height: 18 }} />
        {/* the question. four words, 90px, one amber. nothing else on the frame. */}
        <Line
          words={w(30, 33)}
          size={90}
          accentIdx={[1]}
          weight={800}
          maxWidth={600}
        />
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 2 · THE PHONE CALL  12.10 – 15.85
   A deliberately wordless beat. After a 90px headline the frame needs to breathe,
   and the picture already says it: he is asked, he calls home. The only new
   information is that the thread — which has been lying on the courtyard floor
   since frame 0 — stands up and becomes the cord.
   ═══════════════════════════════════════════════════════════════════════════ */
const PhoneCast: React.FC = () => {
  const man = useSwing(12.35, undefined, { from: -12, drop: 90, idle: 0.6 });
  return (
    <Cast
      src="pp_phoneman"
      x={332}
      y={716 + man.dy * 0.3}
      h={472}
      rot={man.rot}
      opacity={man.opacity}
      scale={man.scale}
      glow={useKeys([[14.4, 0], [14.9, 0.55], [15.6, 0.2]])}
      audit="phoneman"
    />
  );
};

const PhoneType: React.FC = () => (
  <Tag at={14.53} text="घर" x={1012} y={112} size={38} color={C.amber} audit="tag:ghar" />
);

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 3 · THE CORRIDOR  15.85 – 19.00
   The turn. He stops being the man on the phone and becomes the viewer walking in.
   The cord straightens into the corridor's own perspective line — same object,
   third meaning — and the headline is asked from the far end.
   ═══════════════════════════════════════════════════════════════════════════ */
const CorridorCast: React.FC = () => {
  const man = useSwing(16.13, undefined, { from: -10, drop: 80, idle: 0.5 });
  return (
    <Cast
      src="modern_man"
      x={266}
      y={726 + man.dy * 0.28}
      h={498}
      rot={man.rot}
      opacity={man.opacity}
      scale={man.scale}
      flip
      audit="modernman"
    />
  );
};

const CorridorType: React.FC = () => (
  <div
    style={{
      position: "absolute",
      right: 74,
      top: 158,
      maxWidth: 640,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    }}
  >
    <Kicker at={16.13} out={18.9} text="तो आज · सीधे काम की बात" size={25} />
    <div style={{ height: 20 }} />
    <Line words={w(52, 55)} size={94} accentIdx={[0]} weight={800} align="right" maxWidth={600} />
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 4 · THREE PILLARS  19.00 – 22.60
   गोत्र is already standing. प्रवर and सपिंड light up on their own words. The
   plate has three real pillars; the light is hung on them, so nothing vector is
   introduced — LAW 8 is satisfied by using the architecture that is already there.
   ═══════════════════════════════════════════════════════════════════════════ */
const Shaft: React.FC<{ x: number; at: number; lit: number }> = ({ x, at, lit }) => {
  const s = useSpringAt(at, RISE);
  const h = interpolate(s, [0, 1], [0, 428]);
  return (
    <div
      style={{
        position: "absolute",
        left: x - 84,
        top: 664 - h,
        width: 168,
        height: h,
        background: `linear-gradient(180deg, rgba(242,160,60,0) 0%, rgba(242,160,60,${0.1 * lit}) 38%, rgba(242,160,60,${0.34 * lit}) 100%)`,
        filter: "blur(11px)",
        opacity: Math.min(s * 1.6, 1),
      }}
    />
  );
};

const PillarsCast: React.FC = () => (
  <>
    <Shaft x={288} at={19.05} lit={1} />
    <Shaft x={640} at={21.25} lit={1} />
    <Shaft x={992} at={22.02} lit={1} />
  </>
);

const PillarsType: React.FC = () => {
  const bar = (at: number) => useSpringAt(at, RISE);
  const b1 = bar(19.05);
  const b2 = bar(21.25);
  const b3 = bar(22.02);
  const rule = (x: number, s: number) => (
    <div
      style={{
        position: "absolute",
        left: x - interpolate(s, [0, 1], [0, 62]),
        top: 612,
        width: interpolate(s, [0, 1], [0, 124]),
        height: 2,
        background: C.amber,
        opacity: 0.85 * s,
      }}
    />
  );
  return (
    <>
      {rule(288, b1)}
      {rule(640, b2)}
      {rule(992, b3)}
      <Tag at={19.05} text="गोत्र" x={288} y={556} size={46} weight={800} audit="p1" />
      <Tag at={21.25} text="प्रवर" x={640} y={556} size={46} weight={800} audit="p2" />
      <Tag at={22.02} text="सपिंड" x={992} y={556} size={46} weight={800} audit="p3" />
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 5 · THE REGISTER  22.60 – 29.70
   The four questions the episode owes the viewer, stamped into the ledger as four
   objects — not four lines of text. Evidence does not float: these land on a stiff
   spring with no pendulum, each under a Latin numeral and a one-word label.
   Longest frame in the cut (7.1s), so it is the most densely authored.
   ═══════════════════════════════════════════════════════════════════════════ */
const QUESTIONS = [
  { src: "ic_scroll", at: 23.0, n: "०१", label: "क्या हैं", x: 322, y: 314, h: 176 },
  { src: "ic_nameplate", at: 24.32, n: "०२", label: "महत्व", x: 958, y: 306, h: 166 },
  { src: "couple", at: 25.44, n: "०३", label: "शादी", x: 322, y: 638, h: 206 },
  { src: "dna", at: 28.32, n: "०४", label: "विज्ञान", x: 958, y: 648, h: 244 },
];

const QCard: React.FC<{ q: typeof QUESTIONS[number] }> = ({ q }) => {
  const s = useSpringAt(q.at, STAMP);
  const settle = interpolate(s, [0, 1], [1.16, 1]);
  return (
    <Cast
      src={q.src}
      x={q.x}
      y={q.y}
      h={q.h}
      scale={settle}
      opacity={Math.min(s * 1.8, 1)}
      rot={interpolate(s, [0, 1], [-4, 0])}
      audit={`q:${q.src}`}
    />
  );
};

const RegisterCast: React.FC = () => (
  <>
    {QUESTIONS.map((q) => (
      <QCard key={q.src} q={q} />
    ))}
  </>
);

const RegisterType: React.FC = () => (
  <>
    {QUESTIONS.map((q) => (
      <QLabel key={q.src} q={q} />
    ))}
  </>
);

const QLabel: React.FC<{ q: typeof QUESTIONS[number] }> = ({ q }) => {
  const s = useSpringAt(q.at + 0.1, STAMP);
  const u = useSpringAt(q.at + 0.28, RISE);
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: q.x - 60,
          top: q.y - q.h - 46,
          fontFamily: anek,
          fontWeight: 500,
          fontSize: 19,
          color: C.amber,
          opacity: 0.9 * s,
          ...halo,
        }}
      >
        {q.n}
      </div>
      <Tag at={q.at + 0.06} text={q.label} x={q.x} y={q.y + 12} size={33} weight={700} audit={`ql:${q.label}`} />
      <div
        style={{
          position: "absolute",
          left: q.x - interpolate(u, [0, 1], [0, 46]),
          top: q.y + 58,
          width: interpolate(u, [0, 1], [0, 92]),
          height: 1,
          background: C.amber,
          opacity: 0.6 * u,
        }}
      />
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 6 · THE ROOTS  29.70 – 33.05
   One word on the whole frame, and it is set in the tradition's own face.
   The thread leaves the picture plane and goes down into the root mass — the
   graphic match that pays off everything the amber line has been doing since 0:00.
   ═══════════════════════════════════════════════════════════════════════════ */
const RootsCast: React.FC = () => {
  const pulse = useSpringAt(32.36, { damping: 9, stiffness: 90, mass: 1.1 });
  return (
    <div
      style={{
        position: "absolute",
        left: 470,
        top: 430,
        width: 460,
        height: 340,
        background:
          "radial-gradient(60% 60% at 50% 50%, rgba(242,160,60,0.42) 0%, rgba(242,160,60,0.12) 45%, rgba(0,0,0,0) 72%)",
        filter: "blur(14px)",
        opacity: interpolate(pulse, [0, 0.55, 1], [0, 0.95, 0.55]),
      }}
    />
  );
};

const RootsType: React.FC = () => {
  const s = useSpringAt(32.36, { damping: 10, stiffness: 120, mass: 1.0 });
  return (
    <>
      <div style={{ position: "absolute", left: 90, top: 214, maxWidth: 520 }}>
        <Kicker at={29.94} out={32.9} text="इस पूरे विषय की" size={26} color={C.creamDim} />
      </div>
      <div
        style={{
          position: "absolute",
          left: 84,
          top: 250,
          ...devaBlock,
          fontFamily: tiro,
          fontWeight: 400,
          fontSize: 168,
          color: C.amber,
          ...halo,
          opacity: Math.min(s * 1.5, 1),
          transform: `translateY(${interpolate(s, [0, 1], [58, 0])}px)`,
        }}
      >
        जड़
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 7 · THE LAMP VOID  33.05 – 38.55
   Yash's second device: BOXES → HEADING. Two systems rise out of the dark, BOTH
   BLURRED, because the viewer has not been told what is inside either of them yet.
   No readable text inside a box — the blur IS the information. On "दोनों असली हैं"
   both take an amber keyline: neither is the wrong one.
   ═══════════════════════════════════════════════════════════════════════════ */
const SysBox: React.FC<{
  at: number;
  x: number;
  y: number;
  wBox: number;
  hBox: number;
  img: string;
  sharp: number;
  real: number;
  scale?: number;
  audit: string;
}> = ({ at, x, y, wBox, hBox, img, sharp, real, scale = 1, audit }) => {
  const s = useSpringAt(at, RISE);
  const op = Math.min(s * 1.5, 1);
  if (op <= 0.004) return null;
  return (
    <div
      data-audit={audit}
      style={{
        position: "absolute",
        left: x - (wBox * scale) / 2,
        top: y - (hBox * scale) / 2 + interpolate(s, [0, 1], [46, 0]),
        width: wBox * scale,
        height: hBox * scale,
        opacity: op,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${real > 0.02 ? `rgba(242,160,60,${0.28 + 0.6 * real})` : "rgba(255,244,226,0.22)"}`,
        boxShadow:
          real > 0.02
            ? `0 0 ${34 * real}px rgba(242,160,60,${0.34 * real}), 0 26px 40px rgba(0,0,0,0.6)`
            : "0 26px 40px rgba(0,0,0,0.6)",
        background: "rgba(14,11,7,0.5)",
      }}
    >
      <Img
        src={staticFile(`sanatan/cut/${img}.png`)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          padding: 18 * scale,
          boxSizing: "border-box",
          filter: `blur(${interpolate(sharp, [0, 1], [9, 0])}px) saturate(${interpolate(sharp, [0, 1], [0.55, 1])})`,
          opacity: interpolate(sharp, [0, 1], [0.62, 1]),
        }}
      />
    </div>
  );
};

const LampCast: React.FC = () => {
  const real = useKeys([
    [37.3, 0],
    [37.8, 1],
  ]);
  return (
    <>
      <SysBox at={35.32} x={352} y={396} wBox={382} hBox={244} img="vd_sage_seated" sharp={0} real={real} audit="box:arsha" />
      <SysBox at={35.84} x={928} y={396} wBox={382} hBox={244} img="ic_village" sharp={0} real={real} audit="box:laukika" />
    </>
  );
};

const LampType: React.FC = () => (
  <div
    style={{
      position: "absolute",
      left: 0,
      right: 0,
      top: 96,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <Kicker at={33.18} out={35.3} text="गोत्र एक शब्द है — पर" size={25} color={C.creamDim} />
    <div style={{ height: 14 }} />
    <Line words={w(107, 109)} size={76} accentIdx={[0]} weight={800} align="center" maxWidth={900} />
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   FRAME 8 · THE ANCESTOR COLUMN  38.55 – 45.70
   The handover. आर्ष sharpens on its own word, then TRAVELS AND SCALES INTO the
   page heading and stays there — which is what deletes the caption block that
   would otherwise be needed for the next ninety seconds of the episode. The other
   box goes back to being an unopened option.
   Then the thread beads out: आप → पिता → उनके पिता, and runs off the top of frame,
   because the sentence is not finished when the 45 seconds are.
   ═══════════════════════════════════════════════════════════════════════════ */
const ColumnCast: React.FC = () => {
  const sharp = useKeys([
    [39.79, 0],
    [40.35, 1],
  ]);
  // the travel into the heading slot
  const hx = useKeys([[40.4, 352], [41.25, 176]]);
  const hy = useKeys([[40.4, 396], [41.25, 92]]);
  const hs = useKeys([[40.4, 1], [41.25, 0.4]]);
  const otherDim = useKeys([[40.4, 1], [41.25, 0.26]]);
  const rishi = useSwing(42.03, undefined, { from: -13, drop: 96, idle: 0.6 });
  return (
    <>
      <div style={{ opacity: otherDim }}>
        <SysBox at={38.4} x={928} y={396} wBox={382} hBox={244} img="ic_village" sharp={0} real={0} audit="box:laukika2" />
      </div>
      <SysBox
        at={38.4}
        x={hx}
        y={hy}
        wBox={382}
        hBox={244}
        img="vd_sage_seated"
        sharp={sharp}
        real={useKeys([[39.79, 0.4], [40.35, 1]])}
        scale={hs}
        audit="box:arsha2"
      />
      <Cast
        src="rishi"
        x={452}
        y={716 + rishi.dy * 0.3}
        h={438}
        rot={rishi.rot}
        opacity={rishi.opacity}
        scale={rishi.scale}
        glow={useKeys([[42.03, 0], [42.6, 0.5], [43.6, 0.22]])}
        audit="rishi"
      />
    </>
  );
};

const ColumnType: React.FC = () => {
  const head = useSpringAt(41.0, RISE);
  return (
    <>
      {/* the box has become the heading — this is the caption that never got written */}
      <div
        style={{
          position: "absolute",
          left: 268,
          top: 62,
          opacity: Math.min(head * 1.4, 1),
          transform: `translateX(${interpolate(head, [0, 1], [-22, 0])}px)`,
        }}
      >
        <div
          style={{
            ...devaBlock,
            fontFamily: anek,
            fontWeight: 800,
            fontSize: 52,
            color: C.cream,
            ...halo,
          }}
        >
          आर्ष <span style={{ color: C.amber }}>गोत्र</span>
        </div>
        <div style={{ width: 138, height: 2, background: C.amber, opacity: 0.8, marginTop: 8 }} />
      </div>
      {/* the tradition's own voice, in the tradition's own face */}
      <div style={{ position: "absolute", left: 646, top: 306, maxWidth: 430 }}>
        <Kicker at={42.35} text="यानी — ऋषि से जुड़ा हुआ" size={31} face={tiro} color={C.cream} />
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE THREAD — stage level, above every ground, below every headline.
   Six poses, one instance. The reveal only ever goes forward.
   ═══════════════════════════════════════════════════════════════════════════ */
const THREAD: readonly (readonly [number, Curve])[] = [
  // frame 1 — a hairline lying on the courtyard floor. Present, not yet meaningful.
  [0.0, [-70, 626, 330, 662, 900, 640, 1350, 614] as Curve],
  [6.6, [-70, 626, 330, 662, 900, 640, 1350, 614] as Curve],
  [8.4, [-70, 622, 300, 646, 840, 596, 1350, 566] as Curve],
  [11.9, [-70, 622, 300, 646, 840, 596, 1350, 566] as Curve],
  // frame 2 — it stands up and becomes the phone cord
  [12.6, [336, 336, 560, 168, 940, 134, 1360, 262] as Curve],
  [15.6, [336, 336, 560, 168, 940, 134, 1360, 262] as Curve],
  // frame 3 — straightens into the corridor's perspective line
  [16.4, [312, 340, 566, 344, 792, 358, 986, 368] as Curve],
  [18.9, [312, 340, 566, 344, 792, 358, 986, 368] as Curve],
  // frame 4 — passes behind the three pillars
  [19.6, [-70, 462, 340, 430, 940, 430, 1350, 462] as Curve],
  [22.5, [-70, 462, 340, 430, 940, 430, 1350, 462] as Curve],
  // frame 5 — drops to the ledger's edge and gets out of the way
  [23.3, [-70, 690, 320, 656, 960, 656, 1350, 690] as Curve],
  [29.6, [-70, 690, 320, 656, 960, 656, 1350, 690] as Curve],
  // frame 6 — THE DIVE. it leaves the picture plane and goes into the roots.
  [30.6, [-70, 566, 300, 520, 606, 620, 690, 772] as Curve],
  [32.9, [-70, 566, 300, 520, 606, 620, 690, 772] as Curve],
  // frame 7 — re-emerges as the spine between the two systems
  [33.9, [640, 772, 640, 566, 640, 316, 640, 72] as Curve],
  [38.4, [640, 772, 640, 566, 640, 316, 640, 72] as Curve],
  // frame 8 — the lineage sutra, and it does not end inside the shot
  [39.4, [988, 780, 988, 560, 968, 300, 962, -40] as Curve],
  [45.7, [988, 780, 988, 560, 968, 300, 962, -40] as Curve],
];

const ThreadLayer: React.FC = () => {
  const t = useT();
  const c = useCurve(THREAD);
  // it is furniture on the floor until the phone call gives it a job
  const op = useKeys([
    [0, 0.22],
    [6.0, 0.3],
    [12.3, 0.3],
    [12.7, 1],
    [45.7, 1],
  ]);
  const width = useKeys([
    [0, 2],
    [12.3, 2],
    [12.9, 3.4],
    [29.9, 3.4],
    [30.7, 4.6],
    [45.7, 4.2],
  ]);
  const reveal = useKeysLinear([
    [0, 0.55],
    [3.2, 1],
  ]);
  // beads only in the last frame — the generations
  const gen = [
    { at: 43.47, u: 0.2, label: "आप" },
    { at: 43.81, u: 0.46, label: "पिता" },
    { at: 44.93, u: 0.72, label: "उनके पिता" },
  ];
  return (
    <AbsoluteFill style={{ zIndex: 6 }}>
      <Thread c={c} reveal={reveal} width={width} opacity={op} glow={t > 12.2} />
      {t > 43.0 &&
        gen.map((g) => <GenNode key={g.label} c={c} {...g} />)}
    </AbsoluteFill>
  );
};

const GenNode: React.FC<{ c: Curve; at: number; u: number; label: string }> = ({
  c,
  at,
  u,
  label,
}) => {
  const s = useSpringAt(at, STAMP);
  const [x, y] = beadAt(c, u);
  if (s <= 0.01) return null;
  return (
    <>
      <Bead x={x} y={y} r={6 + 3 * s} o={s} />
      <div
        style={{
          position: "absolute",
          left: x + 22,
          top: y - 20,
          ...devaBlock,
          fontFamily: anek,
          fontWeight: 700,
          fontSize: 29,
          color: C.cream,
          ...halo,
          whiteSpace: "nowrap",
          opacity: Math.min(s * 1.5, 1),
          transform: `translateX(${interpolate(s, [0, 1], [-14, 0])}px)`,
        }}
      >
        {label}
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE FILM
   ═══════════════════════════════════════════════════════════════════════════ */
export const Gotra45: React.FC = () => {
  const t = useT();
  const { durationInFrames, fps } = useVideoConfig();
  const live = (d: { a: number; b: number }) => t >= d.a - LEAD_IN && t < d.b + LEAD_OUT;
  return (
    <AbsoluteFill style={{ backgroundColor: C.ink }}>
      <Audio src={staticFile("sanatan/audio/gotra45.mp3")} />

      {live(F.courtyard) && (
        <Frame
          def={F.courtyard}
          first
          insertAfter={2}
          cast={<CourtyardCast />}
          over={<CourtyardType />}
        />
      )}
      {live(F.phone) && (
        <Frame def={F.phone} insertAfter={2} cast={<PhoneCast />} over={<PhoneType />} />
      )}
      {live(F.corridor) && (
        <Frame def={F.corridor} insertAfter={3} cast={<CorridorCast />} over={<CorridorType />} />
      )}
      {live(F.pillars) && (
        <Frame def={F.pillars} insertAfter={2} cast={<PillarsCast />} over={<PillarsType />} />
      )}
      {live(F.register) && (
        <Frame def={F.register} insertAfter={2} cast={<RegisterCast />} over={<RegisterType />} />
      )}
      {live(F.roots) && (
        <Frame def={F.roots} insertAfter={2} cast={<RootsCast />} over={<RootsType />} />
      )}
      {live(F.lamp) && (
        <Frame def={F.lamp} insertAfter={3} cast={<LampCast />} over={<LampType />} />
      )}
      {live(F.column) && (
        <Frame def={F.column} insertAfter={3} cast={<ColumnCast />} over={<ColumnType />} />
      )}

      <ThreadLayer />

      <AbsoluteFill style={{ zIndex: 20 }}>
        <Atmosphere warm={1} />
        <Rail total={durationInFrames / fps} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
